import React, { useEffect, useState } from 'react';
import { HashRouter as Router, Routes, Route } from 'react-router-dom';
import { FaHeart, FaSun, FaMoon, FaDesktop } from 'react-icons/fa';
import SupervisorView from './SupervisorView';
import TechnicianView from './TechnicianView';
import * as Fathom from 'fathom-client';

type Theme = 'system' | 'light' | 'dark';

const App: React.FC = () => {
  const [theme, setTheme] = useState<Theme>(() => {
    const saved = localStorage.getItem('theme') as Theme;
    return saved || 'system';
  });

  useEffect(() => {
    const applyTheme = () => {
      const isDark = theme === 'dark' ||
        (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
      document.documentElement.classList.toggle('dark', isDark);
    };

    applyTheme();
    localStorage.setItem('theme', theme);

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (theme === 'system') applyTheme();
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  useEffect(() => {
    // Initialize Fathom when the app loads
    Fathom.load('WPLJSQGB', {
      includedDomains: ['dipswitch.powerio.com']
    });

    // Track pageviews on route change
    const handleRouteChange = () => {
      Fathom.trackPageview();
    };

    window.addEventListener('popstate', handleRouteChange);

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, []);

  return (
    <Router>
      <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900 transition-colors">
        <main className="flex-grow">
          <Routes>
            <Route path="/" element={<SupervisorView />} />
            <Route path="/supervisor/edit/:encodedConfig" element={<SupervisorView />} />
            <Route path="/technician/:encodedConfig" element={<TechnicianView />} />
          </Routes>
        </main>
        <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 py-6 px-6 mt-8 transition-colors">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
              <div className="flex flex-col items-center sm:items-start gap-2">
                <a href="https://www.powerio.com" target="_blank" rel="noopener noreferrer">
                  <img
                    src="/logo-lightmode.png"
                    alt="powerIO"
                    className="h-8 w-auto dark:hidden"
                  />
                  <img
                    src="/logo-darkmode.png"
                    alt="powerIO"
                    className="h-8 w-auto hidden dark:block"
                  />
                </a>
                <span className="inline-flex items-center gap-1 text-sm text-gray-400 dark:text-gray-500">
                  Made with <FaHeart className="text-red-400" /> by powerIO GmbH
                </span>
              </div>
              <div className="flex-1 flex flex-col items-center gap-4">
                <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                  <button
                    type="button"
                    onClick={() => setTheme('light')}
                    className={`p-2 rounded-md transition-colors ${theme === 'light' ? 'bg-white dark:bg-gray-600 shadow-sm text-yellow-500' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                    aria-label="Light mode"
                  >
                    <FaSun />
                  </button>
                  <button
                    type="button"
                    onClick={() => setTheme('system')}
                    className={`p-2 rounded-md transition-colors ${theme === 'system' ? 'bg-white dark:bg-gray-600 shadow-sm text-blue-500' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                    aria-label="System preference"
                  >
                    <FaDesktop />
                  </button>
                  <button
                    type="button"
                    onClick={() => setTheme('dark')}
                    className={`p-2 rounded-md transition-colors ${theme === 'dark' ? 'bg-white dark:bg-gray-600 shadow-sm text-indigo-500' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                    aria-label="Dark mode"
                  >
                    <FaMoon />
                  </button>
                </div>
                <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-gray-500 dark:text-gray-400">
                  <a href="https://support.powerio.com" target="_blank" rel="noopener noreferrer" className="hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
                    Support
                  </a>
                  <a href="https://www.powerio.com/en/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
                    Privacy
                  </a>
                  <a href="https://www.powerio.com/en/imprint" target="_blank" rel="noopener noreferrer" className="hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
                    Imprint
                  </a>
                </div>
                
              </div>
            </div>
          </div>
        </footer>
      </div>
    </Router>
  );
};

export default App;
